import { useState } from 'preact/hooks';
import { authState } from '../App';
import { api } from '../services/api';
import { LoadingSpinner } from '../components/LoadingSpinner';

export function ProfilePage() {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: authState.value.user?.name || '',
    email: authState.value.user?.email || '',
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // TODO: Implement API call to update user profile
      // const response = await api.updateProfile(formData);
      
      // For now, just simulate success
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSuccess('Profile updated successfully');
      setIsEditing(false);
      
      // Update auth state
      if (authState.value.user) {
        authState.value = {
          ...authState.value,
          user: {
            ...authState.value.user,
            name: formData.name,
            email: formData.email,
          }
        };
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: authState.value.user?.name || '',
      email: authState.value.user?.email || '',
    });
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleLogout = () => {
    api.logout();
    authState.value = {
      isAuthenticated: false,
      isLoading: false,
      user: null,
      church: null,
    };
    window.location.href = '/';
  };

  const user = authState.value.user;
  const church = authState.value.church;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
        <p className="mt-2 text-gray-600">Manage your account information</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* User Information */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Personal Information</h2>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Edit Profile
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            {isEditing ? (
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', (e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            ) : (
              <p className="text-gray-900">{user?.name || 'N/A'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            {isEditing ? (
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', (e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            ) : (
              <p className="text-gray-900">{user?.email || 'N/A'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <p className="text-gray-900 capitalize">{user?.role || 'N/A'}</p>
          </div>

          {isEditing && (
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving && <LoadingSpinner size="small" />}
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Church Information */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Church Information</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Church Name
            </label>
            <p className="text-gray-900">{church?.name || 'N/A'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subdomain
            </label>
            <p className="text-gray-900">{church?.subdomain || 'N/A'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone
            </label>
            <p className="text-gray-900">{church?.timezone || 'N/A'}</p>
          </div>

          <div className="pt-4 mt-4 border-t border-gray-100">
            <h3 className="text-md font-medium text-gray-800 mb-2">Geolocation Settings</h3>
            <p className="text-sm text-gray-600 mb-4">
              Update the church's location using your device's current GPS position. This is used to verify parent check-ins.
            </p>
            <button
              onClick={async () => {
                setIsSaving(true);
                setError(null);
                setSuccess(null);
                
                if (!navigator.geolocation) {
                  setError("Geolocation is not supported by your browser");
                  setIsSaving(false);
                  return;
                }

                navigator.geolocation.getCurrentPosition(
                  async (position) => {
                    try {
                      await api.updateChurchLocation(
                        position.coords.latitude,
                        position.coords.longitude
                      );
                      setSuccess("Church location updated to your current live location successfully.");
                    } catch (err: any) {
                      setError(err.message || 'Failed to update church location');
                    } finally {
                      setIsSaving(false);
                    }
                  },
                  (error) => {
                    setError("Unable to retrieve your location. Please check your browser permissions.");
                    setIsSaving(false);
                  },
                  { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
                );
              }}
              disabled={isSaving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              Set Live Location
            </button>
          </div>
        </div>
      </div>

      {/* Account Actions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Account Actions</h2>
        
        <div className="space-y-4">
          <div>
            <button
              onClick={handleLogout}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Sign Out
            </button>
            <p className="mt-2 text-sm text-gray-600">
              Sign out of your account on this device
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
